/* ==========================================================================
   POS module printing: a saved bill / return on the till's 80mm receipt, and
   the shift close / day close slips in the same format.
   ========================================================================== */

import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { escapeHtml, num, printHtml, printReceipt } from "../posHelpers";
import { getPrintSettingsByBranch } from "../../../../services/AdminApiServices";

/** The receipt header the till prints: Print Setup first, the company as fallback. */
export const useReceiptHeader = () => {
  const { branch } = useSelector((state) => state.branchlist);
  const { companyInfolist } = useSelector((state) => state.companyInfo);
  const { branchsettings } = useSelector((state) => state.settings);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (!branch?.guid) return undefined;
    let cancelled = false;
    getPrintSettingsByBranch(branch.guid)
      .then((s) => !cancelled && setSettings(s?.[0] ?? null))
      .catch(() => !cancelled && setSettings(null));
    return () => {
      cancelled = true;
    };
  }, [branch]);

  return useMemo(() => {
    const s = settings || {};
    const company = companyInfolist?.[0] || {};
    return {
      companyName: s.companyName || company.name || branch?.name || "",
      header1: s.header1 || company.address1,
      header2: s.header2 || company.address2,
      header3: s.header3,
      header4: s.header4,
      header5: s.header5,
      footerText1: s.footerText1,
      footerText2: s.footerText2,
      qr: {
        branchID: branch?.guid,
        enabled: branchsettings?.generateQrCodeInBillPrint === true,
        sellerName: s.companyName || company.name || "",
        vatNumber: company.tin || "",
      },
    };
  }, [settings, companyInfolist, branch, branchsettings]);
};

/** Re-prints a saved POS bill or return (an API PosBill). */
export const printPosBill = (bill, header) => {
  const isReturn = bill?.transType === 3;
  const lines = (bill?.lines || []).map((l) => ({
    name: l.productName || l.details || "Item",
    quantity: num(l.quantity),
    price: num(l.price),
    discountPercent: num(l.discPercentage),
    taxPercent: num(l.taxPercentage),
    notes: "",
  }));

  printReceipt(
    {
      header,
      title: isReturn ? "SALES RETURN" : "",
      numberLabel: isReturn ? "Return" : "Bill",
      entryNumber: bill?.entryNumber,
      invoiceDate: bill?.invDate ? dayjs(bill.invDate).format("DD MMM YYYY, hh:mm A") : "",
      customerName: bill?.customerName,
      cashier: bill?.cashierName,
      lines,
      totals: {
        gross: num(bill?.grossTotal),
        lineDiscount: num(bill?.lineDiscount),
        billDiscount: num(bill?.billDiscount),
        extraCharge: num(bill?.additionalAmt),
        tax: num(bill?.taxTotal),
        roundOff: num(bill?.roundOff),
        net: num(bill?.netTotal),
        units: lines.reduce((t, l) => t + num(l.quantity), 0),
      },
      payments: [
        { label: isReturn ? "Cash refund" : "Cash", amount: bill?.cash },
        { label: "Card", amount: bill?.card },
        { label: "UPI", amount: bill?.upi },
        { label: isReturn ? "Credit note" : "Credit", amount: bill?.credit },
      ],
      paid: num(bill?.paid),
      change: num(bill?.change),
      notes: [bill?.refNo ? `Against bill ${bill.refNo}` : "", bill?.terms || ""].filter(Boolean).join(" · "),
    },
    bill?.invDate
  );
};

const fmt = (v) => num(v).toFixed(2);

/**
 * A shift close or day close slip. `close` is the API's PosShiftClose /
 * PosDayClose; both carry the same totals.
 */
export const printCloseSlip = (kind, close, header) => {
  const isShift = kind === "shift";
  const date = dayjs(close?.transactionDate).format("DD MMM YYYY");
  const row = (label, value, cls = "") => `<tr class="${cls}"><td>${escapeHtml(label)}</td><td class="right">${escapeHtml(value)}</td></tr>`;
  const headerLines = [header?.header1, header?.header2]
    .filter(Boolean)
    .map((l) => `<div class="center">${escapeHtml(l)}</div>`)
    .join("");

  const meta = isShift
    ? [
        ["Counter", close?.counterName || ""],
        ["Shift", `${close?.shiftNo ?? ""}`],
        ["Date", date],
        ["First bill", close?.firstBillAt ? dayjs(close.firstBillAt).format("hh:mm A") : "-"],
        ["Last bill", close?.lastBillAt ? dayjs(close.lastBillAt).format("hh:mm A") : "-"],
      ]
    : [
        ["Date", date],
        ["Shifts", `${close?.shiftCount ?? 0}`],
        ["Next date", dayjs(close?.nextDate).format("DD MMM YYYY")],
      ];

  const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${isShift ? "Shift close" : "Day close"} ${escapeHtml(date)}</title>
    <style>
      @page { size: 80mm auto; margin: 2mm; }
      * { box-sizing: border-box; }
      body { width: 76mm; margin: 0; font-family: "Courier New", monospace; font-size: 11px; color: #000; }
      .center { text-align: center; }
      .right { text-align: right; }
      .bold, .total td { font-weight: 700; }
      .store { font-size: 15px; font-weight: 700; }
      .title { font-size: 13px; font-weight: 700; margin: 2px 0; }
      .rule { border-top: 1px dashed #000; margin: 5px 0; }
      table { width: 100%; border-collapse: collapse; }
      td { padding: 1px 0; }
      .total td { font-size: 13px; }
    </style>
  </head>
  <body>
    <div class="center store">${escapeHtml(header?.companyName || "")}</div>
    ${headerLines}
    <div class="rule"></div>
    <div class="center title">${isShift ? "SHIFT CLOSE" : "DAY CLOSE"}</div>
    <table>${meta.map(([k, v]) => row(k, v)).join("")}</table>
    <div class="rule"></div>
    <table>
      ${row("Bills", `${close?.billCount ?? 0}`)}
      ${row("Sales", fmt(close?.salesTotal))}
      ${row("Returns", `${close?.returnCount ?? 0}  -${fmt(close?.returnTotal)}`)}
      ${row("Discount", fmt(close?.discountTotal))}
      ${row("Addition", fmt(close?.additionalAmt))}
      ${row("Tax", fmt(close?.taxTotal))}
      ${row("Round off", fmt(close?.roundOff))}
      ${row("NET", fmt(close?.netTotal), "total")}
    </table>
    <div class="rule"></div>
    <table>
      ${row("Cash", fmt(close?.cash))}
      ${row("Card", fmt(close?.card))}
      ${row("UPI", fmt(close?.upi))}
      ${row("Credit", fmt(close?.credit))}
    </table>
    <div class="rule"></div>
    <table>
      ${row("Expected cash", fmt(close?.expectedCash))}
      ${row("Counted cash", fmt(close?.countedCash))}
      ${row(num(close?.cashDifference) < 0 ? "Short" : "Excess", fmt(Math.abs(num(close?.cashDifference))), "bold")}
    </table>
    ${close?.remarks ? `<div class="rule"></div><div>${escapeHtml(close.remarks)}</div>` : ""}
    <div class="rule"></div>
    <div>Closed by ${escapeHtml(close?.closedUserName || "-")}</div>
    <div>${escapeHtml(dayjs(close?.closedDate).format("DD MMM YYYY, hh:mm A"))}</div>
    <br />
  </body>
</html>`;

  printHtml(html);
};
